import React from 'react';
import I18n from 'i18n-js';
import SanitizedRender from '@reverbdotcom/commons/src/components/sanitized_render';
import { CoreBumpRecommendationListingCard } from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { formatBid } from './bump_helpers';
import { RCAccordion, RCAlertBox, RCIcon, RCInfoBox } from '@reverbdotcom/cadence/components';
import { withPrefix } from '@reverbdotcom/commons/src/i18n_helpers';
import { REVERB_BUMP, useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { RCText, RCTextUtility } from '@reverbdotcom/cadence/components/RCText/RCText';
import { LightningDuotoneIcon, TrophyDuotoneIcon } from '@reverbdotcom/cadence/icons/react';
import { RecommendedRadioV4 } from './recommended_radio_v4';
import classNames from 'classnames';

const COMPONENT_NAME = 'BumpModalBodyTopPickIteration';

interface Props {
  bid: number;
  handleBidChange: (bid: number) => void;
  listing: CoreBumpRecommendationListingCard.Fragment;
  recommendationAmount: number;
  selectedBid: number;
  successMessage: string;
  failMessage: string;
  hideSubmit?: boolean;
  onSubmit: () => void;
  setShowCustomWarning: React.Dispatch<React.SetStateAction<boolean>>;
  showCustomWarning: boolean;
  minimumBumpBid?: number;
}
const t = withPrefix('discovery.bumpModal');

const BUMP_HELP_CENTER_ARTICLE_ID = '360007185794';
const SELL_THROUGH_PERCENT = '25';

export function BumpModalBodyTopPickIteration(props: Props) {
  const mParticleViewData = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ViewedBumpSlider,
    listing: props.listing,
    startingBumpRatePercent: formatBid(props.bid),
    bumpRecommendationPercent: props.recommendationAmount,
  };

  useViewTracking(mParticleViewData, true);

  const competingCount = props.listing?.csp?.inventory?.newTotal ?? 0;
  const cspPath = props.listing?.csp?.webLink?.path;

  return (
    <div className="bump-modal-top-pick-iteration">
      {props.failMessage &&
        <div className="scaling-mb-4">
          <RCAlertBox type="error">
            <SanitizedRender html={props.failMessage} />
          </RCAlertBox>
        </div>
      }
      {props.successMessage &&
        <div className="scaling-mb-4">
          <RCAlertBox type="success">
            {props.successMessage}
          </RCAlertBox>
        </div>
      }
      <div className="bump-modal-top-pick-iteration__header">
        <RCIcon svgComponent={LightningDuotoneIcon} size="rc-space-20" />
        <div>
          <Translate text="discovery.bumpModal.overviewIteration">
            <b className="text-color-accent"><Translate text="discovery.bumpModal.reverbBump" /></b>
          </Translate>
          <b> </b>
          <Translate text="discovery.bumpModal.overviewTail">
            <b className="weight-semibold"><Translate text="discovery.bumpModal.sellThroughCalloutIteration" args={{ percent: SELL_THROUGH_PERCENT }} /></b>
          </Translate>
        </div>
      </div>
      <div className="d-flex fx-dir-col gap-2">
        <RCTextUtility size="400" weight="bold" >
          {t('setRateIteration')}
        </RCTextUtility>
        <RecommendedRadioV4
          currentBid={props.bid}
          handleBidChange={props.handleBidChange}
          recommendationAmount={props.recommendationAmount}
          showCustomWarning={props.showCustomWarning}
          setShowCustomWarning={props.setShowCustomWarning}
          minimumBumpBid={props.minimumBumpBid}
        />
      </div>
      <FeedbackBox
        competingCount={competingCount - 1} // exclude the current listing
        cspPath={cspPath}
        currentBidPercent={formatBid(props.bid)}
        recommendedBidPercent={props.recommendationAmount}
        selectedBidPercent={formatBid(props.selectedBid)}
      />
      <div className="bump-modal__how-it-works-iteration">
        <RCInfoBox theme="gray" variant="subtle">
          <RCAccordion id="bump-how-it-works" size="small" heading={t('howItWorks')}>
            <p>{t('bumpDescription')}</p>
            <ol className="list-type-numeric">
              <li>
                <Translate text="discovery.bumpModal.bumpStep1">
                  <CoreLink target="_blank" to={useHelpCenterArticleUrl(BUMP_HELP_CENTER_ARTICLE_ID)}>
                    <Translate text="discovery.bumpModal.bumpStep1Link" />
                  </CoreLink>
                </Translate>
              </li>
              <li>
                <div>{t('bumpStep2')}</div>
                <div className="pl-1">
                  <ul className="list-type-disc">
                    <li>{t('bumpExample1')}</li>
                    <li>{t('bumpExample2')}</li>
                    <li>{t('bumpExample3')}</li>
                    <li>{t('bumpExample4')}</li>
                  </ul>
                </div>
              </li>
              <li>
                {t('bumpStep3')}
              </li>
            </ol>
            <div className="mt-3">
              <a target="_blank" rel="noreferrer" href={REVERB_BUMP}>
                {t('readMore')}
              </a>
            </div>
          </RCAccordion>
        </RCInfoBox>
      </div>
      <div className="d-flex fx-dir-col color-secondary gap-1">
        <RCText.Fine size="300">
          {t('footnotePartOne')}
        </RCText.Fine>
        <RCText.Fine size="300">
          <Translate text="discovery.bumpModal.footnotePartTwoIteration">
            <b>
              {t('bumpSellConditionCallout')}
            </b>
          </Translate>
        </RCText.Fine>
      </div>

      {
        !props.hideSubmit && (
          <div className="align-right scaling-mt-8">
            <button
              type="button"
              className="button button--primary size-90 weight-bold"
              disabled={props.showCustomWarning}
              onClick={props.onSubmit}
            >
              {t('save')}
            </button>
          </div>
        )
      }
    </div>
  );
}

function FeedbackBox({
  competingCount = 0,
  cspPath,
  currentBidPercent,
  recommendedBidPercent,
  selectedBidPercent,
}: {
  competingCount?: number;
  cspPath?: string | null;
  currentBidPercent: number;
  recommendedBidPercent: number;
  selectedBidPercent: number;
}) {
  let feedbackStateKey: string;
  let winner: boolean;
  let overbid = false;
  if (selectedBidPercent >= recommendedBidPercent) {
    winner = true;
    feedbackStateKey =
      currentBidPercent === selectedBidPercent ? 'currentlyWinningTopPick' : 'willWinTopPick';
    overbid = selectedBidPercent > recommendedBidPercent;
  } else if (selectedBidPercent === 0) {
    winner = false;
    feedbackStateKey = 'noBump';
  } else {
    winner = false;
    feedbackStateKey =
      currentBidPercent === selectedBidPercent ? 'visibilityIsIncreased' : 'willIncreaseVisibility';
  }

  const topPickCallout = (
    competingCount > 0 ? (
      <Translate
        text={`discovery.bumpModal.feedbackDetails.${feedbackStateKey}`}
        args={{
          count: String(competingCount),
          itemWord: I18n.t(
            competingCount === 1
              ? 'discovery.bumpModal.topPickCalloutItemSingular'
              : 'discovery.bumpModal.topPickCalloutItemPlural',
          ),
        }}
      >
        <b>
          <Translate text={`discovery.bumpModal.feedbackCallout.${feedbackStateKey}`} />
        </b>
      </Translate>
    ) : (
      <Translate text={`discovery.bumpModal.feedbackDetails.noCount.${feedbackStateKey}`}>
        <b>
          <Translate text={`discovery.bumpModal.feedbackCallout.${feedbackStateKey}`} />
        </b>
      </Translate>
    )
  );

  return (
    <div className={classNames('d-flex bump-modal-top-pick-iteration__feedback-box', {
      'bump-modal-top-pick-iteration__feedback-box--winner': winner && !overbid,
    })}>
      <RCInfoBox
        theme="gray"
        variant="subtle-outlined"
        svgComponent={winner ? TrophyDuotoneIcon : undefined}
      >
        <RCText size="400">
          {topPickCallout}
          <span> </span>
          {overbid && <Translate text="discovery.bumpModal.overbidTail" />}
        </RCText>
        {!!cspPath && (
          <CoreLink to={cspPath} target="_blank" rel="noreferrer" className="text-link">
            <RCText size="400">{t('topPickViewProductPage')}</RCText>
          </CoreLink>
        )}
      </RCInfoBox>
    </div>
  );
}
